import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useCatStore } from '../../stores';
import { PawLogo } from '../shared/PawLogo';
import { useUnsavedChanges } from '../shared/UnsavedChangesGuard';
import { DAILY_LOG, SUBQ_FLUIDS } from '../../lib/vocabulary';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: string;
}

const navItems: NavItem[] = [
  // Primary - Most-used daily features
  {
    get label() { return i18n.t('nav.items.dashboard'); }, path: '/dashboard', section: 'Primary',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
  },
  {
    get label() { return DAILY_LOG.nav; }, path: '/daily-log', section: 'Primary',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><rect width="8" height="4" x="8" y="2" rx="1" /><path strokeLinecap="round" d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><path strokeLinecap="round" d="M12 11h4M12 16h4M8 11h.01M8 16h.01" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.guidance'); }, path: '/guidance', section: 'Primary',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
  },

  {
    get label() { return i18n.t('nav.items.howToGuides'); }, path: '/how-to', section: 'Primary',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.5" /><path strokeLinecap="round" d="m5.6 5.6 3.9 3.9M18.4 5.6l-3.9 3.9M18.4 18.4l-3.9-3.9M5.6 18.4l3.9-3.9" /></svg>,
  },

  // Clinical - Health tracking
  {
    get label() { return i18n.t('nav.items.labResults'); }, path: '/lab-results', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2" /><path strokeLinecap="round" d="M8.5 2h7" /><path strokeLinecap="round" d="M14.5 16h-5" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.medications'); }, path: '/medications', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><path strokeLinecap="round" d="m8.5 8.5 7 7" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.weight'); }, path: '/weight', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" strokeLinecap="round" /></svg>,
  },
  {
    get label() { return SUBQ_FLUIDS.label; }, path: '/subq-fluids', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2.5s6 6.5 6 10.5a6 6 0 01-12 0c0-4 6-10.5 6-10.5z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.bloodGlucose'); }, path: '/glucose', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4-5 4 3 4-7 6 9" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.breathingRate'); }, path: '/breathing-rate', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M2 12h4l2-6 3 12 2-6h2a3 3 0 100-6" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.qualityOfLife'); }, path: '/qol', section: 'Clinical',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /></svg>,
  },

  // Vet & Reports
  {
    get label() { return i18n.t('nav.items.vetVisitPrep'); }, path: '/vet-prep', section: 'Vet & Reports',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" d="M4.8 2.3A.3.3 0 105 2H4a2 2 0 00-2 2v5a6 6 0 006 6v0a6 6 0 006-6V4a2 2 0 00-2-2h-1a.2.2 0 10.3.3" /><path strokeLinecap="round" d="M8 15v1a6 6 0 006 6v0a6 6 0 006-6v-4" /><circle cx="20" cy="10" r="2" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.vetVisits'); }, path: '/vet-visits', section: 'Vet & Reports',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="4" width="18" height="18" rx="2" /><path strokeLinecap="round" d="M16 2v4M8 2v4M3 10h18" /><path strokeLinecap="round" d="M9 16l2 2 4-4" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.exportData'); }, path: '/export', section: 'Vet & Reports',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>,
  },

  // Profile & Settings
  {
    get label() { return i18n.t('nav.items.catProfile'); }, path: '/cat-profile', section: 'Settings',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.carePasses'); }, path: '/care-passes', section: 'Settings',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.settings'); }, path: '/settings', section: 'Settings',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z" /><circle cx="12" cy="12" r="3" /></svg>,
  },

  // The one entry addressed to the owner rather than the cat, so it sits in its own
  // section rather than among the tracking surfaces. Not called "Breathing" —
  // /breathing-rate already means the cat's resting respiratory rate.
  {
    get label() { return i18n.t('nav.items.caringForYou'); }, path: '/caring-for-you', section: 'Caring for you',
    icon: <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M4 9h12v5.5a4.5 4.5 0 01-4.5 4.5h-3A4.5 4.5 0 014 14.5V9z" /><path strokeLinecap="round" d="M16 10.5h1.5a2.5 2.5 0 010 5H16" /><path strokeLinecap="round" d="M7.5 3v2.5M12.5 3v2.5" /></svg>,
  },
];

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cats, currentCat, setCurrentCat } = useCatStore();
  const { confirmDiscard } = useUnsavedChanges();

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-faint flex flex-col shadow-soft">
      {/* Logo */}
      <div className="p-5 border-b border-faint">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-sage-600 rounded-xl flex items-center justify-center shadow-xs">
            <PawLogo className="w-5 h-5" color="white" faceColor="#6D28A8" />
          </div>
          <span className="text-lg font-bold text-charcoal">{t('app.name')}</span>
        </div>
      </div>

      {/* Navigation Links.
          The scrollbar is NOT hidden here any more, and the list fades out at its
          bottom edge. Cat Profile, Care Passes, Settings and Caring for you sit
          below the fold on a laptop, and the pinned "My Cats" block underneath
          made the visible list look complete — Care Passes was easy to miss
          entirely. The fade says "there is more", the pinned block's shadow says
          "this is pinned, not the end". */}
      <div className="relative flex-1 min-h-0">
        <nav className="h-full overflow-y-auto px-3 py-4">
        {['Primary', 'Clinical', 'Vet & Reports', 'Settings', 'Caring for you'].map((section) => {
          const sectionItems = navItems.filter((item) => item.section === section);
          if (sectionItems.length === 0) return null;

          return (
            <div key={section}>
              {section !== 'Primary' && <div className="h-px bg-gray-200 my-3" />}
              <ul className="space-y-0.5 mb-2">
                {sectionItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={(e) => {
                        // Warn before leaving a form with unsaved edits; cancel
                        // keeps the owner on the current page. The prompt is the
                        // app's own dialog, so the click is always cancelled first
                        // and the navigation replayed once the owner answers.
                        e.preventDefault();
                        confirmDiscard().then((ok) => {
                          if (ok) navigate(item.path);
                        });
                      }}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm ${
                          isActive
                            ? 'bg-sage-50 text-sage-700 shadow-xs'
                            : 'text-mid hover:bg-slate-50 hover:text-charcoal'
                        }`
                      }
                    >
                      <span className="flex-shrink-0 opacity-80">{item.icon}</span>
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        </nav>
        {/* Bottom fade — purely a cue that the list continues. Non-interactive. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent"
        />
      </div>

      {/* Cat Switcher Section — pinned above the scrolling list, and shadowed so
          it reads as pinned rather than as the end of the navigation. */}
      <div className="border-t border-faint p-4 shadow-[0_-4px_12px_-6px_rgb(45_25_60_/_0.12)]">
        <p className="text-2xs text-soft tracking-wider font-semibold mb-2.5">{t('nav.sidebar.myCats')}</p>
        <div className="space-y-1.5">
          {cats.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCurrentCat(cat)}
              aria-pressed={currentCat?.id === cat.id}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm ${
                currentCat?.id === cat.id
                  ? 'bg-sage-50 text-sage-700 border border-sage-200'
                  : 'text-charcoal hover:bg-slate-50 border border-transparent'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        {cats.length > 0 && (
          <button
            onClick={() => {
              confirmDiscard().then((ok) => {
                if (ok) navigate('/onboarding');
              });
            }}
            className="w-full text-left px-3 py-2.5 mt-2.5 rounded-xl text-sm font-medium text-sage-600 hover:bg-sage-50 transition-colors"
          >
            {t('nav.sidebar.addAnotherCat')}
          </button>
        )}
      </div>
    </aside>
  );
};
